import type { Step } from "react-joyride";

/** Matches Tailwind `lg` breakpoint — tour uses the same cutoff as dashboard mobile tabs. */
export const DASHBOARD_WELCOME_TOUR_NARROW_QUERY = "(max-width: 1023px)";

export const DASHBOARD_TOUR_SELECTORS = {
  chat: '[data-tour="dashboard-chat"]',
  /** Narrow step 1: small anchor so `bottom` placement is not flipped off-screen. */
  chatHeader: '[data-tour="dashboard-chat-header"]',
  siteUrl: '[data-tour="dashboard-site-url"]',
  featuresLink: '[data-tour="dashboard-features-link"]',
  chatInput: '[data-tour="dashboard-chat-input"]',
} as const;

export type DashboardTourTargetSelector =
  (typeof DASHBOARD_TOUR_SELECTORS)[keyof typeof DASHBOARD_TOUR_SELECTORS];

/** Scroll / `before` hook targets on narrow (step 0 uses header, not full column). */
const NARROW_STEP_SCROLL_TARGETS: readonly DashboardTourTargetSelector[] = [
  DASHBOARD_TOUR_SELECTORS.chatHeader,
  DASHBOARD_TOUR_SELECTORS.siteUrl,
  DASHBOARD_TOUR_SELECTORS.featuresLink,
  DASHBOARD_TOUR_SELECTORS.chatInput,
];

const MOBILE_TOUR_Z = 1100;

/** Step 1 (chat): skip Joyride scroll; `isFixed` would disable Joyride scrolling entirely. */
const narrowChatTourFloating: Pick<
  Step,
  "floatingOptions" | "skipScroll" | "isFixed" | "zIndex"
> = {
  floatingOptions: { flipOptions: false },
  skipScroll: true,
  isFixed: true,
  zIndex: MOBILE_TOUR_Z,
};

/**
 * Step 2 (site URL): must allow Joyride to scroll — `isFixed` + `skipScroll` both suppress
 * `needsScrolling()` in react-joyride, which is why custom window scroll never ran alongside the tour.
 */
const narrowSiteUrlTourFloating: Pick<
  Step,
  "floatingOptions" | "skipScroll" | "zIndex"
> = {
  floatingOptions: { flipOptions: false },
  skipScroll: false,
  zIndex: MOBILE_TOUR_Z,
};

/** Step 4 (chat input): chat tab fills the narrow viewport, so skip Joyride scroll like step 1. */
const narrowChatInputTourFloating: Pick<
  Step,
  "floatingOptions" | "skipScroll" | "isFixed" | "zIndex"
> = {
  floatingOptions: { flipOptions: false },
  skipScroll: true,
  isFixed: true,
  zIndex: MOBILE_TOUR_Z,
};

export function getDashboardWelcomeTourStepTemplates(
  isNarrowViewport: boolean,
): Pick<
  Step,
  | "target"
  | "title"
  | "content"
  | "placement"
  | "skipBeacon"
  | "floatingOptions"
  | "skipScroll"
  | "isFixed"
  | "zIndex"
>[] {
  return [
    {
      target: isNarrowViewport
        ? DASHBOARD_TOUR_SELECTORS.chatHeader
        : DASHBOARD_TOUR_SELECTORS.chat,
      title: "Change your website here",
      content: "Tell the assistant what to change.",
      placement: isNarrowViewport ? "bottom" : "left",
      skipBeacon: true,
      ...(isNarrowViewport ? narrowChatTourFloating : {}),
    },
    {
      target: DASHBOARD_TOUR_SELECTORS.siteUrl,
      title: "Your live website",
      content: "Opens the public site.",
      placement: "bottom",
      skipBeacon: true,
      ...(isNarrowViewport ? narrowSiteUrlTourFloating : {}),
    },
    {
      target: DASHBOARD_TOUR_SELECTORS.featuresLink,
      title: "Site checklist",
      content: "What’s filled in, and what still needs a pass.",
      placement: isNarrowViewport ? "auto" : "bottom",
      skipBeacon: true,
    },
    {
      target: DASHBOARD_TOUR_SELECTORS.chatInput,
      title: "Ask here",
      content: "Type a change and send it.",
      placement: "top",
      skipBeacon: true,
      ...(isNarrowViewport ? narrowChatInputTourFloating : {}),
    },
  ];
}

export function getDashboardWelcomeTourTargetForStepIndex(
  index: number,
  opts?: { narrow?: boolean },
): DashboardTourTargetSelector | undefined {
  if (opts?.narrow) {
    return NARROW_STEP_SCROLL_TARGETS[index];
  }
  return [
    DASHBOARD_TOUR_SELECTORS.chat,
    DASHBOARD_TOUR_SELECTORS.siteUrl,
    DASHBOARD_TOUR_SELECTORS.featuresLink,
    DASHBOARD_TOUR_SELECTORS.chatInput,
  ][index];
}
